'use client';

import { Button, Flex, Input, Table, Text } from '@chakra-ui/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { LuPlus } from 'react-icons/lu';

import NewSupplierDialog from '@features/finance/NewSupplierDialog';
import { allSuppliers } from 'helpers/fakeData';
import { useHref } from 'hooks/useHref';

const inputProps = {
  borderColor: 'border.light',
  borderRadius: 'md',
  size: 'sm' as const,
};

const SuppliersPage = () => {
  const router = useRouter();
  const { supplierHref } = useHref();
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const onSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);
  const onOpenDialog = useCallback(() => setDialogOpen(true), []);
  const onDialogOpenChange = useCallback((details: { open: boolean }) => setDialogOpen(details.open), []);
  const onRowClick = useCallback(
    (id: string) => () => {
      router.push(supplierHref(id));
    },
    [router, supplierHref],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') {
      return allSuppliers;
    }
    return allSuppliers.filter(
      (supplier) =>
        supplier.name.toLowerCase().includes(q) ||
        (supplier.email ?? '').toLowerCase().includes(q) ||
        (supplier.walletAddress ?? '').toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <Flex flexDirection="column" gap={4} p={4}>
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Flex direction="column" gap={0.5}>
          <Text fontSize="lg" fontWeight="bold">
            Suppliers
          </Text>
          <Text fontSize="sm" color="text.secondary">
            Who you pay. Save payment info to autofill payment links.
          </Text>
        </Flex>
        <Button size="2xs" borderRadius="md" onClick={onOpenDialog}>
          <LuPlus size={14} />
          New supplier
        </Button>
      </Flex>
      <Input placeholder="Search by name, email, or wallet" value={query} onChange={onSearch} {...inputProps} maxW="sm" />
      <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2}>
        <Table.Root>
          <Table.Header fontSize="sm" fontWeight="medium">
            <Table.Row bg="transparent">
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4}>
                Name
              </Table.ColumnHeader>
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4} textAlign="end">
                Contact
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map((supplier, index) => (
              <Table.Row key={supplier.id} fontSize="sm" cursor="pointer" onClick={onRowClick(supplier.id)}>
                <Table.Cell
                  border="none"
                  p={4}
                  borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
                  borderBottomLeftRadius={index === filtered.length - 1 ? 'xl' : 'none'}
                >
                  <Text fontWeight="medium">{supplier.name}</Text>
                </Table.Cell>
                <Table.Cell
                  border="none"
                  p={4}
                  textAlign="end"
                  color="text.secondary"
                  borderTopRightRadius={index === 0 ? 'xl' : 'none'}
                  borderBottomRightRadius={index === filtered.length - 1 ? 'xl' : 'none'}
                >
                  {supplier.email ?? supplier.phone ?? '—'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Flex>
      <NewSupplierDialog open={dialogOpen} onOpenChange={onDialogOpenChange} />
    </Flex>
  );
};

export default memo(SuppliersPage);
